import React, { useState, useEffect } from 'react';
import { Col, Row, getItemWithExpiration } from '../../lib/utils';
import { colors } from '../../lib/colors';
import { Body3Regular, CaptionLight, Subtitle1Bold } from '../../lib/font';
import Grid, { GridColProps } from '../../components/Grid/Grid';
import { useTodayCheckins, useCheckinLogs, useManualCheckout, useAutoCheckout } from '@/features/checkin/hooks';
import { getEnrollmentStatus, getCheckinMemberDisplayStatus } from '@/features/checkin/helpers';
import MemberCheckinHistoryModal from '@/components/Checkin/MemberCheckinHistoryModal';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import BusinessInfoFooter from '../../components/BusinessInfoFooter';
import { formatKoreanPhoneNumber } from '../../utils/phoneNumber';

dayjs.extend(duration);

const PAGE_LIMIT = 30;

const formatPhone = (phone?: string) => {
    return formatKoreanPhoneNumber(phone, '-');
};

const CheckinDashboardPage = ({ createAlert }: { createAlert: any }) => {
    const [authObject] = useState(getItemWithExpiration('authObject'));
    const centerId = authObject?.centerId;

    // ━━━ 실시간 요약 (오늘) ━━━
    const { data: todayData, refetch } = useTodayCheckins({ centerId });
    const [refreshCountdown, setRefreshCountdown] = useState(30);
    const [showGuide, setShowGuide] = useState(() => localStorage.getItem('guide_checkin') !== 'hidden');
    const [now, setNow] = useState(dayjs());

    useEffect(() => {
        const timer = setInterval(() => {
            setRefreshCountdown((prev) => {
                if (prev <= 1) { refetch(); return 30; }
                return prev - 1;
            });
            setNow(dayjs());
        }, 1000);
        return () => clearInterval(timer);
    }, [refetch]);

    const totalCheckins = todayData?.totalCheckins ?? 0;
    const currentlyIn = todayData?.currentlyIn ?? 0;

    const formatDuration = (checkinAt: string) => {
        const diff = now.diff(dayjs(checkinAt));
        const d = dayjs.duration(diff);
        const h = Math.floor(d.asHours());
        const m = d.minutes();
        if (h > 0) return `${h}시간 ${m}분`;
        return `${m}분`;
    };

    // ━━━ 체크아웃 ━━━
    const checkoutMutation = useManualCheckout();
    const autoCheckoutMutation = useAutoCheckout();

    const handleCheckout = async (logId: string) => {
        if (!logId) {
            createAlert('체크아웃 실패', '체크인 기록 ID를 찾을 수 없습니다.', true);
            return;
        }
        try {
            await checkoutMutation.mutateAsync({ centerId, logId });
            createAlert('체크아웃 완료', '체크아웃 처리되었습니다.', false);
            refetch(); // 오늘 데이터도 갱신
        } catch (err) {
            console.error('checkout error:', err);
            createAlert('체크아웃 실패', '처리에 실패했습니다.', true);
        }
    };

    const handleAutoCheckout = async () => {
        try {
            const result: any = await autoCheckoutMutation.mutateAsync({ centerId });
            createAlert('자동 체크아웃', result?.message || '완료', false);
        } catch {
            createAlert('자동 체크아웃 실패', '처리에 실패했습니다.', true);
        }
    };

    // ━━━ 회원 이력 모달 ━━━
    const [historyModal, setHistoryModal] = useState<{ memberId: string; memberName: string } | null>(null);
    const [memoModal, setMemoModal] = useState<{
        memberName: string;
        phoneNumber: string;
        memo: string;
        checkinAt: string;
    } | null>(null);

    const handleRowClick = (row: any) => {
        // 방문자 메모가 있으면 메모 모달 우선
        if (row.memo && row.memo.trim()) {
            setMemoModal({
                memberName: row.memberName || '방문객',
                phoneNumber: row.phoneNumber || '',
                memo: row.memo,
                checkinAt: row.checkinAt,
            });
            return;
        }
        const mid = row.memberId;
        if (mid) {
            setHistoryModal({ memberId: mid, memberName: row.memberName });
        } else {
            createAlert('출석 이력', '회원 ID가 없는 방문객 기록입니다.', false);
        }
    };

    // ━━━ 수동 일괄 체크아웃 — 확인 플로우 ━━━
    const confirmAutoCheckout = () => {
        createAlert(
            '수동 일괄 체크아웃',
            [
                '90분 이상 이용중인 회원들을 지금 즉시 일괄 체크아웃 처리합니다.',
                '',
                '• 평소엔 10분마다 시스템이 자동으로 정리하므로 별도 조치는 필요 없어요.',
                '• 지금 바로 현황을 정리하고 싶을 때만 수동으로 눌러주세요.',
                '• 출석 기록은 그대로 남으며, 체크아웃 시각만 90분 전 시점으로 기록됩니다.',
                '',
                '진행하시겠어요?',
            ].join('\n'),
            false,
            '진행',
            () => handleAutoCheckout(),
            true,
            '취소',
            () => {},
        );
    };

    // ━━━ 이력 데이터 (필터/페이징) ━━━
    const [startDate, setStartDate] = useState(dayjs().format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD'));
    const [searchInput, setSearchInput] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [page, setPage] = useState(1);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchInput.trim());
            setPage(1);
        }, 400);
        return () => clearTimeout(timer);
    }, [searchInput]);

    const today = dayjs().format('YYYY-MM-DD');
    const isViewingToday = startDate === today && endDate === today && !debouncedSearch;

    const { data: checkinData } = useCheckinLogs({
        centerId,
        startDate,
        endDate,
        search: debouncedSearch || undefined,
        page,
        limit: PAGE_LIMIT,
        enabled: !isViewingToday,
    });

    const todayCheckins = todayData?.checkins ?? [];
    const checkins = isViewingToday ? todayCheckins : (checkinData?.results ?? []);
    const totalResults = isViewingToday ? todayCheckins.length : (checkinData?.totalResults ?? 0);
    const totalPages = isViewingToday ? 1 : (checkinData?.totalPages ?? 1);

    const handleDateChange = (start: string, end: string) => {
        setStartDate(start);
        setEndDate(end);
        setPage(1);
    };

    // ━━━ Grid 컬럼 정의 ━━━
    const colDefs: GridColProps[] = [
        {
            field: 'memberName',
            headerName: '회원명',
            width: 160,
            align: 'center',
            customComponent: (value, row) => {
                // 방문객 메모·관리자 호출 기록만 NEW 뱃지. 퍼블릭 회원은 정식 이용권 회원이라 제외.
                const isVisitor = getEnrollmentStatus(row) === 'visitor';
                const hasMemo = !!(row.memo && row.memo.trim());
                return (
                    <Row style={{ alignItems: 'center', gap: 6, justifyContent: 'center' }}>
                        <span style={{ fontSize: 14, color: '#111827', fontWeight: isVisitor ? 700 : 500 }}>
                            {value || '-'}
                        </span>
                        {isVisitor && (
                            <span
                                style={{
                                    fontSize: 10,
                                    fontWeight: 700,
                                    color: '#FFFFFF',
                                    backgroundColor: '#EF4444',
                                    borderRadius: 10,
                                    padding: '1px 6px',
                                    lineHeight: 1.4,
                                }}
                            >
                                NEW
                            </span>
                        )}
                        {hasMemo && (
                            <span title="방문자 메모 있음 (클릭하면 내용 보기)" style={{ fontSize: 13 }}>
                                📝
                            </span>
                        )}
                    </Row>
                );
            },
        },
        {
            field: 'phoneNumber',
            headerName: '연락처',
            width: 150,
            align: 'center',
            customFormat: (value) => formatPhone(value),
        },
        {
            field: 'memberType',
            headerName: '회원타입',
            width: 120,
            align: 'center',
            customComponent: (_value, row) => {
                const status = getCheckinMemberDisplayStatus(row);
                if (status === 'public') {
                    return <span style={{ fontSize: 13, color: '#2563EB', fontWeight: 500 }}>퍼블릭 회원</span>;
                }
                if (status === 'enrolled') {
                    return <span style={{ fontSize: 13, color: '#047857', fontWeight: 500 }}>수강 회원</span>;
                }
                if (status === 'visitor') {
                    return (
                        <span
                            style={{
                                padding: '3px 10px',
                                borderRadius: 999,
                                fontSize: 12,
                                fontWeight: 700,
                                backgroundColor: '#DBEAFE',
                                color: '#1D4ED8',
                                border: '1px solid #BFDBFE',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            방문객
                        </span>
                    );
                }
                // no_pass: 원래 회원이지만 체크인 시점 이용권/PT 없음
                return (
                    <span
                        style={{
                            padding: '3px 10px',
                            borderRadius: 999,
                            fontSize: 12,
                            fontWeight: 700,
                            backgroundColor: '#FEF3C7',
                            color: '#B45309',
                            border: '1px solid #FDE68A',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        이용권 없음
                    </span>
                );
            },
        },
        {
            field: 'checkinAt',
            headerName: '체크인',
            type: 'date',
            dateFormat: 'YYYY-MM-DD HH:mm',
            width: 160,
            align: 'center',
            isSort: true,
        },
        {
            field: 'checkoutAt',
            headerName: '체크아웃',
            width: 180,
            align: 'center',
            customComponent: (value, row) => {
                if (value) {
                    return <span style={{ fontSize: 13 }}>{dayjs(value).format('YYYY-MM-DD HH:mm')}</span>;
                }
                if (dayjs(row.checkinAt).isSame(dayjs(), 'day')) {
                    return (
                        <Row style={{ alignItems: 'center', gap: 6, justifyContent: 'center' }}>
                            <span style={{
                                width: 6, height: 6, borderRadius: '50%',
                                backgroundColor: '#22C55E', display: 'inline-block',
                            }} />
                            <span style={{ fontSize: 12, color: '#16A34A', fontWeight: 500 }}>
                                이용중 ({formatDuration(row.checkinAt)})
                            </span>
                            <button
                                style={{
                                    fontSize: 11, padding: '2px 8px',
                                    borderRadius: 4, border: '1px solid #D1D5DB',
                                    backgroundColor: '#fff', color: '#6B7280',
                                    cursor: 'pointer',
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleCheckout(row._id || row.id);
                                }}
                            >
                                체크아웃
                            </button>
                        </Row>
                    );
                }
                return <span style={{ color: colors.LAVEL_1 }}>-</span>;
            },
        },
        {
            field: 'method',
            headerName: '방법',
            width: 120,
            align: 'center',
            customComponent: (value) => {
                // 특수한 메서드(방문자 메모/관리자 호출)만 강조, 일반 키오스크/QR/수동은 담담한 텍스트
                if (value === 'visitor_memo') {
                    return (
                        <span
                            style={{
                                padding: '3px 10px',
                                borderRadius: 6,
                                fontSize: 12,
                                fontWeight: 700,
                                backgroundColor: '#FEF3C7',
                                color: '#B45309',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            방문자 메모
                        </span>
                    );
                }
                if (value === 'admin_call') {
                    return (
                        <span
                            style={{
                                padding: '3px 10px',
                                borderRadius: 6,
                                fontSize: 12,
                                fontWeight: 700,
                                backgroundColor: '#FEE2E2',
                                color: '#B91C1C',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            관리자 호출
                        </span>
                    );
                }
                const labels: Record<string, string> = {
                    kiosk_phone: '키오스크',
                    qr: 'QR',
                    manual: '수동',
                };
                return (
                    <span style={{ fontSize: 13, color: '#6B7280' }}>{labels[value] || value || '-'}</span>
                );
            },
        },
        {
            field: 'activeSubscriptions',
            headerName: '활성 이용권',
            customComponent: (value) => {
                if (value?.length) {
                    return (
                        <Col style={{ gap: 2 }}>
                            {value.map((sub: any, idx: number) => {
                                const period = sub.startDate && sub.endDate
                                    ? `${dayjs(sub.startDate).format('YYYY.MM.DD')} ~ ${dayjs(sub.endDate).format('YYYY.MM.DD')}`
                                    : '';
                                return (
                                    <Row key={idx} style={{ alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                        <span
                                            style={{
                                                width: 5,
                                                height: 5,
                                                borderRadius: '50%',
                                                backgroundColor: '#10B981',
                                                flexShrink: 0,
                                            }}
                                        />
                                        <CaptionLight style={{ color: '#065F46', fontWeight: 600 }}>
                                            {sub.productName}
                                        </CaptionLight>
                                        {sub.lockerNumber && (
                                            <span
                                                style={{
                                                    fontSize: 11,
                                                    fontWeight: 600,
                                                    color: '#92400E',
                                                    backgroundColor: '#FEF3C7',
                                                    border: '1px solid #FDE68A',
                                                    borderRadius: 4,
                                                    padding: '1px 6px',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {sub.lockerNumber}번
                                            </span>
                                        )}
                                        {period && (
                                            <span style={{ fontSize: 11, color: '#6B7280', whiteSpace: 'nowrap' }}>
                                                {period}
                                            </span>
                                        )}
                                    </Row>
                                );
                            })}
                        </Col>
                    );
                }
                return <span style={{ fontSize: 13, color: '#D1D5DB' }}>-</span>;
            },
        },
    ];

    // ━━━ 날짜 프리셋 ━━━
    const datePresets = [
        { label: '오늘', start: dayjs().format('YYYY-MM-DD'), end: dayjs().format('YYYY-MM-DD') },
        { label: '이번주', start: dayjs().startOf('week').format('YYYY-MM-DD'), end: dayjs().format('YYYY-MM-DD') },
        { label: '이번달', start: dayjs().startOf('month').format('YYYY-MM-DD'), end: dayjs().format('YYYY-MM-DD') },
        { label: '지난달', start: dayjs().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'), end: dayjs().subtract(1, 'month').endOf('month').format('YYYY-MM-DD') },
    ];

    const inputStyle: React.CSSProperties = {
        border: '1px solid #E1E4E6',
        borderRadius: 8,
        padding: '8px 12px',
        fontSize: 14,
        outline: 'none',
    };

    return (
        <Col
            style={{
                width: '100%',
                height: '100%',
                backgroundColor: '#F8FAFC',
                marginTop: 64,
            }}
        >
            <Col className="w-full px-[48px] pt-[32px]">
                <Row style={{ alignItems: 'center', gap: 8 }}>
                    <Subtitle1Bold>출석 관리</Subtitle1Bold>
                    {!showGuide && (
                        <button onClick={() => { setShowGuide(true); localStorage.removeItem('guide_checkin'); }}
                            style={{ width: 22, height: 22, borderRadius: '50%', border: '1.5px solid #6EE7B7', backgroundColor: '#F0FDF4', color: '#059669', fontSize: 12, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>?</button>
                    )}
                </Row>
                {showGuide && (
                    <Col style={{ marginTop: 12, padding: '18px 22px', backgroundColor: '#F0FDF4', borderRadius: 12, border: '1px solid #A7F3D0', position: 'relative' }}>
                        <button onClick={() => { setShowGuide(false); localStorage.setItem('guide_checkin', 'hidden'); }}
                            style={{ position: 'absolute', top: 10, right: 14, background: 'none', border: 'none', fontSize: 16, color: '#6EE7B7', cursor: 'pointer' }}>✕</button>
                        <span style={{ fontSize: 14, fontWeight: 700, color: '#065F46', marginBottom: 6 }}>출석 관리란?</span>
                        <Col style={{ gap: 3, fontSize: 13, color: '#065F46', lineHeight: 1.7 }}>
                            <span>회원의 <b>키오스크 체크인 현황</b>을 실시간으로 확인하고 관리하는 페이지입니다.</span>
                            <span>현재 센터 이용 중인 회원 수, 오늘 총 출석 수를 한눈에 파악할 수 있습니다.</span>
                            <span style={{ marginTop: 4 }}>
                                ⏱️ <b>90분 이상 미체크아웃 건은 10분마다 시스템이 자동 정리</b>합니다. 지금 바로 정리하고 싶다면 아래 "수동 일괄 체크아웃" 버튼을 눌러주세요.
                            </span>
                        </Col>
                    </Col>
                )}

                {/* ━━━ 실시간 요약 카드 ━━━ */}
                <Row style={{ marginTop: 20, gap: 16 }}>
                    <Col style={{ flex: 1, backgroundColor: '#EFF6FF', borderRadius: 12, padding: 20 }}>
                        <CaptionLight color={colors.LAVEL_1}>오늘 총 출석</CaptionLight>
                        <Subtitle1Bold style={{ marginTop: 4 }}>{totalCheckins}명</Subtitle1Bold>
                    </Col>
                    <Col style={{ flex: 1, backgroundColor: '#F0FDF4', borderRadius: 12, padding: 20 }}>
                        <Row style={{ alignItems: 'center', gap: 6 }}>
                            <span style={{
                                width: 6, height: 6, borderRadius: '50%',
                                backgroundColor: '#22C55E', display: 'inline-block',
                            }} />
                            <CaptionLight color={colors.LAVEL_1}>현재 이용중</CaptionLight>
                        </Row>
                        <Subtitle1Bold style={{ marginTop: 4, color: '#00A793' }}>
                            {currentlyIn}명
                        </Subtitle1Bold>
                    </Col>
                    <Col
                        style={{
                            flex: 1,
                            backgroundColor: '#FEF3C7',
                            borderRadius: 12,
                            padding: 20,
                            border: '1px solid #FDE68A',
                        }}
                    >
                        <Row style={{ alignItems: 'center', gap: 6 }}>
                            <span style={{ fontSize: 14 }}>🆕</span>
                            <CaptionLight color={colors.LAVEL_1}>오늘 방문객 메모</CaptionLight>
                        </Row>
                        <Subtitle1Bold style={{ marginTop: 4, color: '#B45309' }}>
                            {todayCheckins.filter((c: any) => c.method === 'visitor_memo' || c.method === 'admin_call').length}명
                        </Subtitle1Bold>
                    </Col>
                    <Col style={{ flex: 1, backgroundColor: '#F8F9FA', borderRadius: 12, padding: 20 }}>
                        <CaptionLight color={colors.LAVEL_1}>자동 새로고침</CaptionLight>
                        <Row style={{ alignItems: 'center', gap: 6, marginTop: 4 }}>
                            <Subtitle1Bold>{refreshCountdown}초</Subtitle1Bold>
                            <button
                                style={{ fontSize: 11, color: '#3B82F6', background: 'none', border: 'none', cursor: 'pointer' }}
                                onClick={() => { refetch(); setRefreshCountdown(30); }}
                            >
                                즉시 새로고침
                            </button>
                        </Row>
                    </Col>
                </Row>

                {/* ━━━ 필터 영역 ━━━ */}
                <Col style={{ marginTop: 28, gap: 12 }}>
                    <Row style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        {datePresets.map((preset) => {
                            const sel = startDate === preset.start && endDate === preset.end;
                            return (
                                <button
                                    key={preset.label}
                                    style={{
                                        padding: '6px 14px',
                                        borderRadius: 16,
                                        fontSize: 12,
                                        fontWeight: sel ? 600 : 400,
                                        backgroundColor: sel ? '#3B82F6' : '#F3F4F6',
                                        color: sel ? '#fff' : '#4B5563',
                                        border: sel ? '1px solid #3B82F6' : '1px solid #D1D5DB',
                                        cursor: 'pointer',
                                    }}
                                    onClick={() => handleDateChange(preset.start, preset.end)}
                                >
                                    {preset.label}
                                </button>
                            );
                        })}
                        <span style={{ fontSize: 12, color: '#D1D5DB', margin: '0 4px' }}>|</span>
                        <input
                            style={inputStyle}
                            type="date"
                            value={startDate}
                            onChange={(e) => handleDateChange(e.target.value, endDate)}
                        />
                        <span style={{ fontSize: 14, color: '#6B7280' }}>~</span>
                        <input
                            style={inputStyle}
                            type="date"
                            value={endDate}
                            onChange={(e) => handleDateChange(startDate, e.target.value)}
                        />
                    </Row>

                    <Row style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                        <Row style={{ gap: 8, alignItems: 'center' }}>
                            <input
                                style={{ ...inputStyle, width: 280 }}
                                placeholder="회원명 또는 연락처 검색"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                            />
                            {searchInput && (
                                <button
                                    style={{ fontSize: 12, color: '#9CA3AF', background: 'none', border: 'none', cursor: 'pointer' }}
                                    onClick={() => { setSearchInput(''); setDebouncedSearch(''); setPage(1); }}
                                >
                                    ✕ 초기화
                                </button>
                            )}
                        </Row>
                        <Row style={{ gap: 8, alignItems: 'center' }}>
                            {currentlyIn > 0 && (
                                <button
                                    title="90분 이상 미체크아웃 건을 지금 즉시 정리 (평소엔 10분마다 자동 처리)"
                                    style={{
                                        padding: '6px 12px', borderRadius: 6, fontSize: 12,
                                        border: '1px solid #F59E0B', backgroundColor: '#FFFBEB',
                                        color: '#B45309', cursor: 'pointer', fontWeight: 500,
                                    }}
                                    onClick={confirmAutoCheckout}
                                >
                                    수동 일괄 체크아웃
                                </button>
                            )}
                            <Body3Regular style={{ color: colors.LAVEL_1 }}>
                                총 {totalResults.toLocaleString()}건
                            </Body3Regular>
                            {totalPages > 1 && (
                                <CaptionLight style={{ color: colors.LAVEL_1 }}>
                                    ({page} / {totalPages} 페이지)
                                </CaptionLight>
                            )}
                        </Row>
                    </Row>
                </Col>
            </Col>

            {/* ━━━ 데이터 그리드 ━━━ */}
            <Col className="w-full px-[48px]" style={{ marginTop: 8 }}>
                <Grid
                    colDefs={colDefs}
                    rowData={checkins}
                    isInfinity={true}
                    onClickRow={handleRowClick}
                />
            </Col>

            {/* ━━━ 서버 사이드 페이지네이션 ━━━ */}
            {totalPages > 1 && (
                <Row className="w-full px-[48px]" style={{ gap: 4, alignItems: 'center', padding: '16px 48px 32px', justifyContent: 'center' }}>
                    <PaginationButton label="≪" disabled={page <= 1} onClick={() => setPage(1)} />
                    <PaginationButton label="‹ 이전" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} />

                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum: number;
                        if (totalPages <= 5) { pageNum = i + 1; }
                        else if (page <= 3) { pageNum = i + 1; }
                        else if (page >= totalPages - 2) { pageNum = totalPages - 4 + i; }
                        else { pageNum = page - 2 + i; }
                        const sel = pageNum === page;
                        return (
                            <button
                                key={pageNum}
                                style={{
                                    width: 32, height: 32, borderRadius: 6, fontSize: 13,
                                    fontWeight: sel ? 700 : 400,
                                    backgroundColor: sel ? '#3B82F6' : '#fff',
                                    color: sel ? '#fff' : '#374151',
                                    border: sel ? '1px solid #3B82F6' : '1px solid #D1D5DB',
                                    cursor: 'pointer',
                                }}
                                onClick={() => setPage(pageNum)}
                            >
                                {pageNum}
                            </button>
                        );
                    })}

                    <PaginationButton label="다음 ›" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} />
                    <PaginationButton label="≫" disabled={page >= totalPages} onClick={() => setPage(totalPages)} />
                </Row>
            )}

            {/* ━━━ 회원 출석 이력 모달 ━━━ */}
            <MemberCheckinHistoryModal
                isVisible={!!historyModal}
                onClose={() => setHistoryModal(null)}
                centerId={centerId}
                memberId={historyModal?.memberId ?? ''}
                memberName={historyModal?.memberName ?? ''}
            />

            {/* ━━━ 방문자 메모 모달 ━━━ */}
            {memoModal && (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        backgroundColor: 'rgba(15,23,42,0.5)',
                        zIndex: 1000,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                    onClick={() => setMemoModal(null)}
                >
                    <Col
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            width: 440,
                            backgroundColor: 'white',
                            borderRadius: 16,
                            padding: '24px 28px',
                            boxShadow: '0 20px 50px rgba(15,23,42,0.2)',
                        }}
                    >
                        <Row style={{ alignItems: 'center', gap: 8, marginBottom: 4 }}>
                            <span style={{ fontSize: 18 }}>📝</span>
                            <span style={{ fontSize: 17, fontWeight: 700, color: '#111827' }}>
                                방문자 메모
                            </span>
                        </Row>
                        <span style={{ fontSize: 12, color: '#9CA3AF' }}>
                            {dayjs(memoModal.checkinAt).format('YYYY-MM-DD HH:mm')} 체크인
                        </span>

                        <Col
                            style={{
                                marginTop: 16,
                                padding: '14px 16px',
                                backgroundColor: '#F9FAFB',
                                borderRadius: 10,
                                border: '1px solid #E5E7EB',
                                gap: 6,
                            }}
                        >
                            <Row style={{ alignItems: 'center', gap: 6 }}>
                                <span style={{ fontSize: 12, color: '#6B7280' }}>이름</span>
                                <span style={{ fontSize: 14, fontWeight: 700, color: '#111827' }}>
                                    {memoModal.memberName}
                                </span>
                            </Row>
                            {memoModal.phoneNumber && (
                                <Row style={{ alignItems: 'center', gap: 6 }}>
                                    <span style={{ fontSize: 12, color: '#6B7280' }}>연락처</span>
                                    <span style={{ fontSize: 13, color: '#374151' }}>
                                        {formatPhone(memoModal.phoneNumber)}
                                    </span>
                                </Row>
                            )}
                        </Col>

                        <Col
                            style={{
                                marginTop: 12,
                                padding: '14px 16px',
                                backgroundColor: '#FFFBEB',
                                borderRadius: 10,
                                border: '1px solid #FDE68A',
                            }}
                        >
                            <span style={{ fontSize: 12, fontWeight: 700, color: '#B45309', marginBottom: 6 }}>
                                💬 메모 내용
                            </span>
                            <span
                                style={{
                                    fontSize: 14,
                                    color: '#1F2937',
                                    lineHeight: 1.6,
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                }}
                            >
                                {memoModal.memo}
                            </span>
                        </Col>

                        <Row style={{ marginTop: 20, justifyContent: 'flex-end' }}>
                            <button
                                onClick={() => setMemoModal(null)}
                                style={{
                                    padding: '8px 22px',
                                    borderRadius: 8,
                                    fontSize: 13,
                                    fontWeight: 600,
                                    backgroundColor: '#3B82F6',
                                    color: 'white',
                                    border: 'none',
                                    cursor: 'pointer',
                                }}
                            >
                                닫기
                            </button>
                        </Row>
                    </Col>
                </div>
            )}

            {/* 사업자 정보 (카드사 심사용) */}
            <div style={{ width: '100%', marginTop: 40 }}>
                <BusinessInfoFooter />
            </div>
        </Col>
    );
};

const PaginationButton = ({ label, disabled, onClick }: { label: string; disabled: boolean; onClick: () => void }) => (
    <button
        style={{
            padding: '6px 10px', borderRadius: 6, fontSize: 12,
            border: '1px solid #D1D5DB', backgroundColor: '#fff',
            color: disabled ? '#D1D5DB' : '#374151',
            cursor: disabled ? 'default' : 'pointer',
        }}
        disabled={disabled}
        onClick={onClick}
    >
        {label}
    </button>
);

export default CheckinDashboardPage;
